import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShareNetwork, Copy, DownloadSimple, Tag } from "@phosphor-icons/react";

export const fmtMoney = (v) => `$${Number(v || 0).toLocaleString()}`;

const loadImg = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Fetch a (possibly cross-origin) image as a same-origin blob URL so the canvas is never tainted.
const fetchImgUrl = async (url) => {
  const res = await fetch(url, { mode: "cors" });
  if (!res.ok) throw new Error("image fetch failed");
  return URL.createObjectURL(await res.blob());
};

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = (text || "").split(" ");
  let line = "";
  let cy = y;
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy);
      line = w;
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line, x, cy);
  return cy + lineHeight;
};

// Draws the branded flip/sale card on a 1080x1350 canvas and returns a PNG blob.
const buildShareCard = async (story) => {
  const hasFound = Number(story.found_price) > 0;
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FDFBF7";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#0D1B2A";
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, W - 6, H - 6);

  try {
    await Promise.all([
      document.fonts.load('700 66px "Cormorant Garamond"'),
      document.fonts.load('italic 400 30px "Cormorant Garamond"'),
      document.fonts.load('700 24px "Outfit"'),
      document.fonts.load('600 30px "Outfit"'),
    ]);
    await document.fonts.ready;
  } catch (e) { /* fonts optional */ }

  const M = 64;
  const imgX = 32, imgY = 32, imgW = W - 64, imgH = 620;
  let objUrl = null;
  try {
    if (story.image_url) {
      objUrl = await fetchImgUrl(story.image_url);
      const img = await loadImg(objUrl);
      const tr = imgW / imgH;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (img.width / img.height > tr) { sw = img.height * tr; sx = (img.width - sw) / 2; }
      else { sh = img.width / tr; sy = (img.height - sh) / 2; }
      ctx.drawImage(img, sx, sy, sw, sh, imgX, imgY, imgW, imgH);
    } else {
      ctx.fillStyle = "#F5F3ED";
      ctx.fillRect(imgX, imgY, imgW, imgH);
    }
  } catch (e) {
    ctx.fillStyle = "#F5F3ED";
    ctx.fillRect(imgX, imgY, imgW, imgH);
  } finally {
    if (objUrl) URL.revokeObjectURL(objUrl);
  }

  if (story.source) {
    ctx.font = '700 24px "Outfit"';
    const label = story.source.toUpperCase();
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = "#0D1B2A";
    ctx.fillRect(imgX + 20, imgY + 20, tw + 36, 50);
    ctx.fillStyle = "#FDFBF7";
    ctx.fillText(label, imgX + 38, imgY + 53);
  }

  let cy = imgY + imgH + 84;
  ctx.fillStyle = "#0D1B2A";
  ctx.font = '700 66px "Cormorant Garamond"';
  cy = wrapText(ctx, story.title, M, cy, W - 2 * M, 76) + 18;

  ctx.fillStyle = "#8A867D";
  ctx.font = '700 24px "Outfit"';
  ctx.fillText(hasFound ? "PAID → SOLD" : "SOLD FOR", M, cy);
  cy += 82;

  ctx.fillStyle = "#0D1B2A";
  ctx.font = '700 88px "Cormorant Garamond"';
  ctx.fillText(hasFound ? `${fmtMoney(story.found_price)} → ${fmtMoney(story.sold_price)}` : fmtMoney(story.sold_price), M, cy);
  if (hasFound && story.multiple) {
    ctx.fillStyle = "#B47A3C";
    ctx.font = '700 46px "Cormorant Garamond"';
    const mtxt = `${story.multiple}× return`;
    ctx.fillText(mtxt, W - M - ctx.measureText(mtxt).width, cy);
  }
  cy += 56;

  ctx.strokeStyle = "rgba(10,10,10,0.15)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(M, cy);
  ctx.lineTo(W - M, cy);
  ctx.stroke();
  cy += 58;

  ctx.fillStyle = "#525252";
  ctx.font = '600 30px "Outfit"';
  ctx.fillText(`— ${story.seller_name}`, M, cy);
  try {
    const logo = await loadImg("/vv_logo.jpg");
    ctx.fillStyle = "#0D1B2A";
    ctx.font = '700 30px "Outfit"';
    const bn = "Vintage Vantage";
    const bnw = ctx.measureText(bn).width;
    ctx.fillText(bn, W - M - bnw, cy);
    const ls = 46, lx = W - M - bnw - ls - 14, ly = cy - 34;
    ctx.save();
    ctx.beginPath();
    ctx.arc(lx + ls / 2, ly + ls / 2, ls / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(logo, lx, ly, ls, ls);
    ctx.restore();
  } catch (e) { /* logo optional */ }

  ctx.fillStyle = "#8A867D";
  ctx.font = 'italic 400 30px "Cormorant Garamond"';
  ctx.fillText("List your fantastic finds — in record time.", M, H - 68);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
};

export function ShareFlipDialog({ story, onClose }) {
  const open = !!story;
  const [generating, setGenerating] = useState(false);
  const hasFound = story && Number(story.found_price) > 0;
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const caption = story
    ? (hasFound
        ? `🏆 ${story.title} — paid ${fmtMoney(story.found_price)}, sold ${fmtMoney(story.sold_price)}${story.multiple ? ` (${story.multiple}× return)` : ""}. Found & flipped with Vintage Vantage.`
        : `🎉 ${story.title} — sold for ${fmtMoney(story.sold_price)}${story.source ? ` on ${story.source}` : ""}. Listed with Vintage Vantage.`)
    : "";

  const doShare = async () => {
    const data = { title: "Vintage Vantage — my win", text: caption, url: shareUrl };
    try {
      if (navigator.share) { await navigator.share(data); return; }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(`${caption} ${shareUrl}`);
      toast.success("Share caption copied to your clipboard");
    } catch (e) {
      toast.error("Couldn't copy — select the caption and copy it manually");
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(`${caption} ${shareUrl}`);
      toast.success("Caption copied");
    } catch (e) {
      toast.error("Couldn't copy caption");
    }
  };

  const downloadImage = async () => {
    if (!story) return;
    setGenerating(true);
    try {
      const blob = await buildShareCard(story);
      if (!blob) throw new Error("no blob");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vintage-vantage-${(story.title || "card").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Share image downloaded — post it anywhere!");
    } catch (e) {
      toast.error("Couldn't generate the image — try 'Copy caption' instead");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-flip-dialog">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 mb-1"><ShareNetwork size={20} weight="fill" /> <span className="label-caps">Share your win</span></div>
          <DialogTitle className="font-serif-display text-2xl font-bold tracking-tight">{hasFound ? "Show off the flip" : "Share the win"}</DialogTitle>
          <DialogDescription className="text-[#525252]">A little social proof goes a long way — share it and pull more buyers in.</DialogDescription>
        </DialogHeader>
        {story && (
          <div className="border border-[#0D1B2A] bg-[#FDFBF7] overflow-hidden" data-testid="share-card">
            {story.image_url && (
              <div className="relative aspect-[16/10] overflow-hidden bg-[#F5F3ED]">
                <img src={story.image_url} alt={story.title} className="h-full w-full object-cover" />
                {story.source && (
                  <div className="absolute top-3 left-3 bg-[#0D1B2A] text-[#FDFBF7] text-xs font-bold uppercase tracking-wide px-2 py-1 inline-flex items-center gap-1"><Tag size={11} weight="fill" /> {story.source}</div>
                )}
              </div>
            )}
            <div className="p-5">
              <p className="font-serif-display text-2xl font-bold leading-tight mb-3">{story.title}</p>
              <div className="flex items-end justify-between gap-2">
                <div>
                  <p className="label-caps mb-0.5">{hasFound ? "Paid → Sold" : "Sold for"}</p>
                  <p className="font-serif-display text-3xl font-bold leading-none">{hasFound ? `${fmtMoney(story.found_price)} → ${fmtMoney(story.sold_price)}` : fmtMoney(story.sold_price)}</p>
                </div>
                {hasFound && story.multiple ? <span className="text-lg font-bold text-[#B47A3C]">{story.multiple}× return</span> : null}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#0D1B2A]/10">
                <span className="text-sm font-semibold text-[#8A867D]">— {story.seller_name}</span>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold"><img src="/vv_logo.jpg" className="h-5 w-5 rounded-full object-cover" alt="" /> Vintage Vantage</span>
              </div>
            </div>
          </div>
        )}
        <button onClick={downloadImage} disabled={generating} data-testid="share-download-button" className="w-full h-14 mt-1 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.98] disabled:opacity-60">
          <DownloadSimple size={19} weight="bold" /> {generating ? "Creating image…" : "Download image"}
        </button>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <button onClick={doShare} data-testid="share-now-button" className="h-14 border border-[#0D1B2A] rounded-none font-medium inline-flex items-center justify-center gap-2 hover:bg-[#F5F3ED] transition-colors"><ShareNetwork size={18} weight="fill" /> Share</button>
          <button onClick={copyCaption} data-testid="share-copy-button" className="h-14 border border-[#0D1B2A] rounded-none font-medium inline-flex items-center justify-center gap-2 hover:bg-[#F5F3ED] transition-colors"><Copy size={18} /> Copy caption</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
